import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Paper,
  Stack,
  Chip,
  Alert,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Code as CodeIcon,
  Info as InfoIcon,
  DragIndicator as DragIcon,
  Upload as UploadIcon,
  TextFields as TextFieldsIcon,
  Numbers as NumbersIcon,
  Tag as IntegerIcon,
  CheckBox as BooleanIcon,
  List as ArrayIcon,
  List as EnumIcon,
  Help as HelpIcon,
} from '@mui/icons-material';

const OutputSchemaEditor = ({ open, onClose, initialSchema, onSave }) => {
  const [schemaName, setSchemaName] = useState('');
  const [properties, setProperties] = useState([]);
  const [errors, setErrors] = useState({});
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const [importError, setImportError] = useState('');
  
  // Inicializar com schema existente
  useEffect(() => {
    if (initialSchema && open) {
      setSchemaName(initialSchema.name || '');
      setProperties(initialSchema.properties || []);
      setErrors({});
    } else if (open && !initialSchema) {
      // Resetar ao abrir sem schema
      setSchemaName('');
      setProperties([]);
      setErrors({});
    }
  }, [initialSchema, open]);
  
  const propertyTypes = [
    { value: 'string', label: 'String', description: 'Texto simples' },
    { value: 'number', label: 'Number', description: 'Decimal (ex: 0.95)' },
    { value: 'integer', label: 'Integer', description: 'Inteiro (ex: 42)' },
    { value: 'boolean', label: 'Boolean', description: 'true ou false' },
    { value: 'array', label: 'Array', description: 'Lista de strings' },
    { value: 'enum', label: 'Enum', description: 'Lista de opções' },
  ];
  
  const addProperty = () => {
    setProperties([...properties, {
      name: '',
      type: 'string',
      description: '',
      enum_values: []
    }]);
  };
  
  const removeProperty = (index) => {
    setProperties(properties.filter((_, i) => i !== index));
    // Limpar erros relacionados
    const newErrors = { ...errors };
    Object.keys(newErrors).forEach(key => {
      if (key.startsWith(`prop_${index}_`)) {
        delete newErrors[key];
      }
    });
    setErrors(newErrors);
  };
  
  const updateProperty = (index, field, value) => {
    const updated = [...properties];
    updated[index] = { ...updated[index], [field]: value };
    setProperties(updated);
    
    // Limpar erro ao editar
    if (errors[`prop_${index}_${field}`]) {
      const newErrors = { ...errors };
      delete newErrors[`prop_${index}_${field}`];
      setErrors(newErrors);
    }
  };
  
  const handleImportJson = () => {
    setImportError('');
    try {
      const parsed = JSON.parse(jsonInput);
      
      // Validar estrutura básica
      if (!parsed.name) {
        setImportError('O JSON deve conter um campo "name"');
        return;
      }
      
      if (!parsed.properties || !Array.isArray(parsed.properties)) {
        setImportError('O JSON deve conter um array "properties"');
        return;
      }
      
      // Importar dados
      setSchemaName(parsed.name);
      setProperties(parsed.properties.map(prop => ({
        name: prop.name || '',
        type: prop.type || 'string',
        description: prop.description || '',
        enum_values: prop.enum_values || []
      })));
      
      // Fechar dialog e limpar
      setImportDialogOpen(false);
      setJsonInput('');
      setErrors({});
    } catch (error) {
      setImportError(`JSON inválido: ${error.message}`);
    }
  };
  
  const validate = () => {
    const newErrors = {};
    
    // Validar nome do schema
    if (!schemaName.trim()) {
      newErrors.schemaName = 'Nome do schema é obrigatório';
    } else if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(schemaName)) {
      newErrors.schemaName = 'Nome deve começar com letra e conter apenas letras, números e _';
    }
    
    // Validar propriedades
    if (properties.length === 0) {
      newErrors.properties = 'Adicione pelo menos uma propriedade';
    }
    
    const propertyNames = new Set();
    properties.forEach((prop, index) => {
      // Nome obrigatório
      if (!prop.name.trim()) {
        newErrors[`prop_${index}_name`] = 'Nome obrigatório';
      } else if (!/^[a-z][a-z0-9_]*$/i.test(prop.name)) {
        newErrors[`prop_${index}_name`] = 'Nome inválido (use letras, números e _)';
      } else if (propertyNames.has(prop.name)) {
        newErrors[`prop_${index}_name`] = 'Nome duplicado';
      } else {
        propertyNames.add(prop.name);
      }
      
      // Validar enum
      if (prop.type === 'enum') {
        if (!prop.enum_values || prop.enum_values.length === 0) {
          newErrors[`prop_${index}_enum`] = 'Enum precisa de pelo menos um valor';
        } else if (prop.enum_values.some(v => !v || v.trim() === '')) {
          newErrors[`prop_${index}_enum`] = 'Valores não podem estar vazios';
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSave = () => {
    if (!validate()) {
      return;
    }
    
    const schema = {
      name: schemaName.trim(),
      type: 'object',
      properties: properties.map(prop => ({
        name: prop.name.trim(),
        type: prop.type,
        description: prop.description.trim() || '',
        ...(prop.type === 'enum' && { 
          enum_values: prop.enum_values.filter(v => v && v.trim()).map(v => v.trim())
        })
      }))
    };
    
    onSave(schema);
    onClose();
  };
  
  const handleCancel = () => {
    onClose();
  };
  
  const getTypeIcon = (type) => {
    const iconStyle = { fontSize: 18 };
    switch (type) {
      case 'string': return <TextFieldsIcon sx={iconStyle} />;
      case 'number': return <NumbersIcon sx={iconStyle} />;
      case 'integer': return <IntegerIcon sx={iconStyle} />;
      case 'boolean': return <BooleanIcon sx={iconStyle} />;
      case 'array': return <ArrayIcon sx={iconStyle} />;
      case 'enum': return <EnumIcon sx={iconStyle} />;
      default: return <HelpIcon sx={iconStyle} />;
    }
  };
  
  return (
    <>
    <Dialog 
      open={open} 
      onClose={handleCancel} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh', maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CodeIcon color="primary" />
            <Typography variant="h6">Configurar Structured Output</Typography>
            <Tooltip title="Define um schema JSON para o agente retornar dados estruturados">
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          
          {/* Botão Importar JSON no header */}
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setImportDialogOpen(true)}
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Importar JSON
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          O modelo irá gerar um objeto JSON que segue exatamente este schema
        </Typography>
      </DialogTitle>
      
      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Nome do Schema */}
          <TextField
            fullWidth
            label="Nome do Schema"
            value={schemaName}
            onChange={(e) => {
              setSchemaName(e.target.value);
              if (errors.schemaName) {
                setErrors({ ...errors, schemaName: undefined });
              }
            }}
            placeholder="ex: ClassificacaoEnum, DadosContato"
            error={!!errors.schemaName}
            helperText={errors.schemaName || 'Nome que identifica este schema (PascalCase recomendado)'}
            required
            autoFocus
          />
          
          <Divider />
          
          {/* Lista de Propriedades */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Propriedades do Schema
              </Typography>
              <Chip 
                label={`${properties.length} campo${properties.length !== 1 ? 's' : ''}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
            
            {errors.properties && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.properties}
              </Alert>
            )}
            
            <Stack spacing={2}>
              {properties.map((prop, index) => (
                <Paper 
                  key={index} 
                  variant="outlined" 
                  sx={{ 
                    p: 2,
                    position: 'relative',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  {/* Header da propriedade */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
                    <Box sx={{ 
                      mt: 0.5, 
                      color: 'text.disabled',
                      cursor: 'move'
                    }}>
                      <DragIcon />
                    </Box>
                    
                    <TextField
                      label="Nome do Campo"
                      value={prop.name}
                      onChange={(e) => updateProperty(index, 'name', e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                      error={!!errors[`prop_${index}_name`]}
                      helperText={errors[`prop_${index}_name`]}
                      placeholder="ex: classificacao, nome, email"
                      required
                    />
                    
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel>Tipo</InputLabel>
                      <Select
                        value={prop.type}
                        label="Tipo"
                        onChange={(e) => {
                          const newType = e.target.value;
                          const updated = [...properties];
                          updated[index] = { 
                            ...updated[index], 
                            type: newType,
                            // Limpar enum_values se mudar de enum para outro tipo
                            ...(newType !== 'enum' && { enum_values: [] })
                          };
                          setProperties(updated);
                        }}
                        renderValue={(value) => (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getTypeIcon(value)}
                            <Typography variant="body2">
                              {propertyTypes.find(t => t.value === value)?.label}
                            </Typography>
                          </Box>
                        )}
                      >
                        {propertyTypes.map(type => (
                          <MenuItem key={type.value} value={type.value}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                              <Box sx={{ mt: 0.5 }}>
                                {getTypeIcon(type.value)}
                              </Box>
                              <Box>
                                <Typography variant="body2">
                                  {type.label}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {type.description}
                                </Typography>
                              </Box>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    <Tooltip title="Remover propriedade">
                      <IconButton 
                        color="error" 
                        onClick={() => removeProperty(index)}
                        size="small"
                        sx={{ mt: 0.5 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  
                  {/* Descrição */}
                  <TextField
                    fullWidth
                    label="Descrição"
                    value={prop.description}
                    onChange={(e) => updateProperty(index, 'description', e.target.value)}
                    size="small"
                    multiline
                    rows={2}
                    placeholder="Descrição clara para o modelo entender o campo..."
                    helperText="Quanto mais claro, melhor o modelo entende o que retornar"
                  />
                  
                  {/* Enum Values */}
                  {prop.type === 'enum' && (
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        fullWidth
                        label="Valores do Enum (separados por vírgula)"
                        value={prop.enum_values?.join(', ') || ''}
                        onChange={(e) => {
                          const values = e.target.value
                            .split(',')
                            .map(v => v.trim())
                            .filter(Boolean);
                          updateProperty(index, 'enum_values', values);
                        }}
                        size="small"
                        placeholder="duvida, reclamacao, elogio"
                        error={!!errors[`prop_${index}_enum`]}
                        helperText={errors[`prop_${index}_enum`] || 'Lista de valores que o campo pode ter'}
                        required
                      />
                      
                      {prop.enum_values && prop.enum_values.length > 0 && (
                        <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {prop.enum_values.map((val, i) => (
                            <Chip 
                              key={i} 
                              label={val} 
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}
                </Paper>
              ))}
            </Stack>
            
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addProperty}
              fullWidth
              sx={{ mt: 2 }}
            >
              Adicionar Propriedade
            </Button>
          </Box>
          
          {/* Preview do Schema */}
          {properties.length > 0 && schemaName && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CodeIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" fontWeight={600}>
                  Preview do Schema JSON
                </Typography>
              </Box>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'grey.900',
                  color: '#4CAF50',
                  fontFamily: '"Cascadia Code", "Fira Code", Monaco, monospace',
                  fontSize: '0.8rem',
                  overflow: 'auto',
                  maxHeight: 200,
                  border: '1px solid rgba(76, 175, 80, 0.3)',
                }}
              >
                <pre style={{ margin: 0 }}>
                  {JSON.stringify(
                    {
                      name: schemaName || 'SchemaName',
                      type: 'object',
                      properties: properties.map(p => ({
                        name: p.name || 'campo',
                        type: p.type,
                        description: p.description || '',
                        ...(p.type === 'enum' && p.enum_values?.length > 0 && {
                          enum_values: p.enum_values
                        })
                      }))
                    },
                    null,
                    2
                  )}
                </pre>
              </Paper>
            </Box>
          )}
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2, pt: 2 }}>
        <Button onClick={handleCancel} color="inherit">
          Cancelar
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={!schemaName || properties.length === 0}
          startIcon={<CodeIcon />}
        >
          Salvar Schema
        </Button>
      </DialogActions>
    </Dialog>
    
    {/* Dialog de Importação JSON */}
    <Dialog
      open={importDialogOpen}
      onClose={() => {
        setImportDialogOpen(false);
        setJsonInput('');
        setImportError('');
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadIcon color="primary" />
          <Typography variant="h6">Importar Schema JSON</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Cole o JSON completo do schema para importar todos os campos automaticamente
        </Typography>
      </DialogTitle>
      
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            fullWidth
            multiline
            rows={16}
            value={jsonInput}
            onChange={(e) => setJsonInput(e.target.value)}
            placeholder={`{
  "name": "AnaliseAvaliacaoCliente",
  "type": "object",
  "properties": [
    {
      "name": "sentimento",
      "type": "enum",
      "description": "Classificação do sentimento",
      "enum_values": ["positivo", "neutro", "negativo"]
    },
    {
      "name": "confianca",
      "type": "number",
      "description": "Nível de confiança (0 a 1)"
    }
  ]
}`}
            sx={{
              fontFamily: '"Cascadia Code", "Fira Code", Monaco, monospace',
              fontSize: '0.85rem',
              '& .MuiInputBase-input': {
                fontFamily: '"Cascadia Code", "Fira Code", Monaco, monospace',
              }
            }}
          />
          
          {importError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {importError}
            </Alert>
          )}
          
          <Alert severity="info" variant="outlined">
            <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
              <strong>Formato esperado:</strong>
            </Typography>
            <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
              • <strong>name</strong>: Nome do schema (obrigatório)
            </Typography>
            <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
              • <strong>type</strong>: "object" (obrigatório)
            </Typography>
            <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
              • <strong>properties</strong>: Array com os campos (obrigatório)
            </Typography>
            <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
              Cada propriedade pode ter: name, type, description, enum_values
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2, pt: 2 }}>
        <Button 
          onClick={() => {
            setImportDialogOpen(false);
            setJsonInput('');
            setImportError('');
          }}
          color="inherit"
        >
          Cancelar
        </Button>
        <Button 
          onClick={handleImportJson}
          variant="contained"
          startIcon={<UploadIcon />}
          disabled={!jsonInput.trim()}
        >
          Importar
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default OutputSchemaEditor;

